import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Spinner,
  QueryBuilderType,
  QueryBuilder,
  BooleanOperator,
  TextArea,
  Checkbox,
} from '@scale/scaleui';
import {
  ResumeSearchResultsBody,
  RESUME_SEARCH_MAX_RESULT_WINDOW,
  ResumeSearchEventType,
} from '@scale/shared-types';
import { useMutation, useQuery } from 'react-query';
import { logResumeSearchEvent, searchResumes } from './api';
import { ResumeSearchResultsTable } from './ResumeSearchResultsTable';
import { StartSearchCard } from 'react-scale/components/GrowthCenter/ResumeSearch/StartSearchCard';
import Pagination from 'react-scale/components/design-system/Pagination';
import { RESUME_SEARCH_DISPLAY_PAGE_SIZE } from 'react-scale/components/GrowthCenter/ResumeSearch/constants';
import { useUser } from 'react-scale/components/ui/UserContext';

type PaginatedData = ResumeSearchResultsBody[];

const MAX_PAGES = RESUME_SEARCH_MAX_RESULT_WINDOW / RESUME_SEARCH_DISPLAY_PAGE_SIZE;

export const ResumeSearch = () => {
  const requestingUser = useUser();

  const [queries, setQueries] = useState<QueryBuilderType>({
    filters: [''],
    queryOperator: BooleanOperator.AND,
  });
  const [data, setData] = useState<PaginatedData[] | undefined>(); // grouped by page number
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [showStartSearch, setShowStartSearch] = useState(true);

  // need to save search query to use on pagination, even if input fields change
  const [searchQuery, setSearchQuery] = useState<QueryBuilderType>({
    filters: [],
    queryOperator: BooleanOperator.AND,
  });

  // user ids to narrow search scope by
  const [rawIds, setRawIds] = useState<string>('');
  const [showIdInput, setShowIdInput] = useState(false);

  const ids = useMemo(() => {
    if (!showIdInput) {
      return [];
    }
    return rawIds
      .split(',')
      .map((id: string) => id.trim().replace(/^"+|"+$/g, ''))
      .filter(Boolean);
  }, [rawIds, showIdInput]);

  const {
    isLoading,
    isFetching,
    data: rawData,
    refetch,
  } = useQuery(
    ['resume-search'],
    async () =>
      await searchResumes({
        queries,
        ids,
        includeCount: true,
        page: 0,
      }),
    { enabled: false },
  );

  const { isLoading: isLoadingMore, data: moreRawData } = useQuery(
    ['resume-search-get-page', page],
    async () => {
      return await searchResumes({ queries: searchQuery, ids, page });
    },
    { enabled: !!page && data && !data[page] }, // only refetch when page has not been previously fetched
  );

  const { mutate: logEvent } = useMutation(
    async (resultCount: number) =>
      await logResumeSearchEvent({
        type: ResumeSearchEventType.Query,
        userId: requestingUser.id,
        data: { numSearchResults: resultCount, stringifiedQuery: JSON.stringify(queries) },
      }),
  );

  useEffect(() => {
    if (rawData) {
      setShowStartSearch(false);

      setData([rawData.results]);
      setCount(rawData.count);
      logEvent(rawData.count);
    }
  }, [rawData]);

  useEffect(() => {
    if (moreRawData) {
      const newData = data!.slice(); // make a copy of data
      newData[page] = moreRawData.results;
      setData(newData);
    }
  }, [moreRawData]);

  const search = () => {
    setPage(0);
    setSearchQuery(queries);
    refetch();
  };

  const tableData = useMemo(() => {
    return data ? data[page] ?? [] : [];
  }, [data, page]);

  const numPages = useMemo(() => {
    const realPages = Math.ceil(count / RESUME_SEARCH_DISPLAY_PAGE_SIZE);
    return Math.min(realPages, MAX_PAGES);
  }, [count]);

  const fetchPage = (i: number) => {
    const pageNumber = i - 1; // i is 1-indexed
    setPage(pageNumber);
  };

  // bfs to find whether there exists at least 1 string in queries
  const isEmpty = useMemo(() => {
    const stack: (QueryBuilderType | string)[] = [queries];
    while (stack.length > 0) {
      const curr = stack.pop();

      if (typeof curr === 'string') {
        if (curr !== '') {
          return false;
        }
      } else {
        for (const child of (curr as QueryBuilderType).filters) {
          stack.push(child);
        }
      }
    }

    return true;
  }, [queries]);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <div>Each query phrase can either be searched with exact match or fuzzy search.</div>
        <div>
          To specify exact search, put quotations around the query phrase (e.g.
          <em>"computer science"</em>). To specify fuzzy search, leave query phrase without
          quotations (e.g. <em>computer science</em>).
        </div>
        <div className="w-[750px]">
          <QueryBuilder filters={queries} setFilters={setQueries} />
        </div>
        <Checkbox
          checked={showIdInput}
          label="Search within specific contributors"
          onChange={() => setShowIdInput(!showIdInput)}
        />
        {showIdInput && (
          <TextArea
            helperText="To specify a subset of contributors to search within, type a comma-separated list of contributor ids."
            value={rawIds}
            onChange={e => setRawIds(e.target.value)}
            className="w-[725px]"
            initialResize
            label="Contributor IDs"
            resizable
            rows={2}
          />
        )}

        <div className="flex gap-4 ">
          <Button size="lg" disabled={isEmpty} onClick={search} isLoading={isLoading || isFetching}>
            Search
          </Button>
        </div>
      </div>

      {showStartSearch ? (
        <StartSearchCard />
      ) : !data ? (
        <Spinner size="lg" />
      ) : (
        <div className="flex flex-col gap-8">
          <ResumeSearchResultsTable
            data={tableData}
            count={count}
            queries={queries}
            ids={ids}
            isLoading={isLoadingMore}
          />

          {/* right now, opensearch pagination only supports window of 10k; trying to paginate out will error */}
          {count > 0 && (
            <Pagination
              size="large"
              className="w-full"
              count={numPages}
              page={page + 1}
              onChange={fetchPage}
            />
          )}
        </div>
      )}
    </div>
  );
};
